// Boards: ShareSheet (D4), ShareSheetNotFound (D4b); canvas note n16.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { t, tx } from '../../i18n';
import { Bdi, textDir } from '../../i18n/direction';
import { LevelTag, WarnIcon, isBlank } from './parts';

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
);


export default function ShareSheet({ deckId, name, onClose, onAuthFailure }) {
  const [list, setList] = useState(null); // shares oldest first; stays null after a failed load
  const [loaded, setLoaded] = useState(false);
  const [username, setUsername] = useState('');
  const [level, setLevel] = useState('view');
  const [shareError, setShareError] = useState(null); // a refusal code or 'general'; cleared by any edit of the field
  const [stuck, setStuck] = useState(false); // list load or remove failed: stays until the sheet opens again
  const busy = useRef({}); // one request at a time per action
  const live = useRef(true);
  const path = `decks/${encodeURIComponent(deckId)}/shares`;

  useEffect(() => {
    live.current = true;
    (async () => {
      try {
        const res = await axios.get(path);
        if (live.current) setList(res.data.shares);
      } catch (e) {
        if (!live.current || await onAuthFailure(e)) return;
        setStuck(true);
      }
      if (live.current) setLoaded(true);
    })();
    return () => { live.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loaded) return null;

  const once = async (action, request) => {
    if (busy.current[action]) return;
    busy.current[action] = true;
    try {
      await request();
    } finally {
      busy.current[action] = false;
    }
  };

  const share = () => {
    if (isBlank(username)) return;
    once('share', async () => {
      setShareError(null);
      try {
        const res = await axios.post(path, { username, level });
        if (!live.current) return;
        const row = res.data;
        // A level change comes back with the same id and goes last; the same level leaves the list as it is.
        setList(l => (l === null || l.some(s => s.id === row.id && s.level === row.level)
          ? l : [...l.filter(s => s.id !== row.id), row]));
        setUsername('');
        setLevel('view');
      } catch (e) {
        if (!live.current || await onAuthFailure(e)) return;
        const detail = e.response?.data?.detail;
        setShareError(detail === 'user_not_found' || detail === 'own_deck' ? detail : 'general');
      }
    });
  };

  const remove = row => once('remove', async () => {
    const drop = () => setList(l => l && l.filter(s => s.id !== row.id));
    try {
      await axios.delete(`${path}/${encodeURIComponent(row.id)}`);
      if (live.current) drop();
    } catch (e) {
      if (!live.current || await onAuthFailure(e)) return;
      if (e.response?.status === 404) drop();
      else setStuck(true);
    }
  });

  const message = shareError === 'user_not_found' ? t('share.userNotFound')
    : shareError === 'own_deck' ? t('share.ownDeck')
      : shareError || stuck ? t('error.general') : null;
  return (
    <>
      <div className="scrim"></div>
      <section className="sheet share-sheet" role="dialog" aria-modal="true" aria-labelledby="share-t" style={{ gap: 14 }}>
        <span className="grab" aria-hidden="true"></span>
        <p id="share-t" className="h-m">{tx('share.title', { name })}</p>
        <div className="field">
          <label className="label" htmlFor="share-user">{t('common.username')}</label>
          <input id="share-user" className={shareError ? 'input input-error' : 'input'} dir={textDir(username)} value={username}
            aria-invalid={shareError ? 'true' : undefined} aria-describedby={message ? 'share-err' : undefined}
            onChange={e => { setUsername(e.target.value); setShareError(null); }} />
          {message && <div id="share-err" className="err" role="alert"><WarnIcon />{message}</div>}
        </div>
        <div className="seg" role="radiogroup" aria-label={t('share.level')}>
          {[['view', t('share.view')], ['edit', t('share.edit')]].map(([value, label]) => (
            <button key={value} type="button" className={level === value ? 'seg-btn seg-on' : 'seg-btn'} role="radio"
              aria-checked={level === value ? 'true' : 'false'} onClick={() => setLevel(value)}>{label}</button>
          ))}
        </div>
        <p className="hint">{t('share.levelsHint')}</p>
        <button type="button" className="btn btn-primary btn-block" onClick={share}>{t('deck.share')}</button>
        {list && list.length > 0
          ? (
            <>
              <h2 className="sec">{t('share.sharedWith')}</h2>
              <ul className="list">
                {list.map(s => (
                  <li key={s.id} className="item" style={{ minHeight: 56, paddingInlineEnd: 4 }}>
                    <span className="item-main"><span className="item-title" style={{ font: '500 15px var(--sans)' }}><Bdi>{s.username}</Bdi></span></span>
                    <LevelTag level={s.level} />
                    <button type="button" className="icon-btn" aria-label={t('share.remove', { name: s.username })} onClick={() => remove(s)}><XIcon /></button>
                  </li>
                ))}
              </ul>
            </>
          )
          : <div style={{ flex: 1 }}></div>}
        <button type="button" className="btn btn-dark btn-block" onClick={onClose}>{t('common.done')}</button>
      </section>
    </>
  );
}
